import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import { cloneDeep, isEmpty, isEqual, remove } from "lodash";

import type {
    IAlarmCentral,
    ICommunicationConfiguration,
    IEnumService,
    IResponseWithLoading,
} from "App/Modules/Models";
import { CommunicationInterfaceType, Protocol } from "App/Modules/Models";
import type { IAlarmCentralsRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve, IIpValidationService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditAlarmCentralInformationComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "mmAlarmCentralsRepository", "mmEnumService", "irIpValidationService"];

    public alarmCentral: IAlarmCentral;

    public originalAlarmCentral: IAlarmCentral;

    public errorMessage: string;

    public numberPattern = "\\+?[\\d ]+";

    public protocols: string[];

    public editResponse: IResponseWithLoading<IAlarmCentral>;

    public resolve: IFormContainerResolve<IAlarmCentral, IAlarmCentral>;

    constructor(
        private readonly $scope: IScope,
        private readonly alarmCentralsRepository: IAlarmCentralsRepository,
        private readonly enumService: IEnumService,
        private readonly ipValidationService: IIpValidationService,
    ) {}

    public $onInit() {
        this.protocols = this.enumService.enumToArray(Protocol).map((p) => p.value);
        remove(this.protocols, (p) => {
            return p === "RTSP" || p === "RTSP_H264" || p === "RTSP_MJPG";
        });

        this.originalAlarmCentral = this.resolve.bindings;
        this.alarmCentral = cloneDeep(this.originalAlarmCentral);

        if (!this.alarmCentral.communicationConfiguration) {
            this.alarmCentral.communicationConfiguration = {
                ip: {
                    externalPort: 443,
                    protocol: Protocol[Protocol.HTTPS],
                },
                type: CommunicationInterfaceType.WiredIP,
                typeName: "WiredIP",
            } as ICommunicationConfiguration;
        }
    }

    public edit() {
        const patchObject = {} as IAlarmCentral;

        if (
            this.alarmCentral.communicationConfiguration &&
            !isEqual(this.originalAlarmCentral.communicationConfiguration, this.alarmCentral.communicationConfiguration)
        ) {
            patchObject.communicationConfiguration = this.alarmCentral.communicationConfiguration;
        }

        if (this.originalAlarmCentral.name !== this.alarmCentral.name) {
            patchObject.name = this.alarmCentral.name;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.alarmCentralsRepository.patchAlarmCentral(this.resolve.bindings.id, patchObject),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IAlarmCentral>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public changePort() {
        const { protocol } = this.alarmCentral.communicationConfiguration.ip;
        if (protocol) {
            this.alarmCentral.communicationConfiguration.ip.externalPort =
                this.ipValidationService.getDefaultProtocolPort(
                    this.alarmCentral.communicationConfiguration.ip.protocol,
                );
        }
    }

    public isHttp(): boolean {
        return this.ipValidationService.isHttp(this.alarmCentral.communicationConfiguration.ip);
    }

    public isWrongPortForProtocol(): boolean {
        if (this.alarmCentral.communicationConfiguration.ip) {
            return this.ipValidationService.isWrongPortForProtocol(this.alarmCentral.communicationConfiguration.ip);
        }
        return false;
    }
}
